import React from 'react';

import PropTypes from 'prop-types';

import { Text } from '@adsk/alloy-react-box';
import { displayDate, VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import { useLocale } from '@adsk/alloy-react-locale';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { getDiffFromToday } from '../../utils/date';

interface DateRelativeProps {
  date: Date;
  toolTipContent: boolean;
}

const DateRelativeText: React.FC<DateRelativeProps> = ({
  date,
  toolTipContent = false,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));

  const dayDiff = getDiffFromToday(date);

  let subText = null;
  if (dayDiff > 0 && dayDiff <= 3) {
    subText =
      dayDiff === 1
        ? T.translate('DUE_TOMORROW')
        : T.translate('DUE_IN_X_DAYS', { days: dayDiff });
  } else if (dayDiff < 0) {
    subText =
      dayDiff === -1
        ? T.translate('OVERDUE_YESTERDAY')
        : T.translate('OVERDUE_X_DAYS', { days: Math.abs(dayDiff) });
  } else if (dayDiff === 0) {
    subText = T.translate('DUE_TODAY');
  }

  if (toolTipContent) {
    return <span>{subText}</span>;
  }
  return <Text color={dayDiff < 0 ? 'red700' : 'adskBlue500'}>{subText}</Text>;
};

const DateToolTipContent: React.FC<DateProps> = ({ date, relative }) => {
  const { locale } = useLocale(({ locale }) =>
    require(`../../i18n/${locale}.json`),
  );
  if (!date) {
    return <span>-</span>;
  }
  const dateToDisplay = displayDate(date, {
    format: VALID_DATE_FORMATS.DEFAULT,
    locale,
  });

  return (
    <div>
      {dateToDisplay}
      {relative && (
        <>
          <br />
          <DateRelativeText date={date} toolTipContent={true} />
        </>
      )}
    </div>
  );
};

interface DateProps {
  date?: Date;
  relative?: boolean;
}

const DateCell: React.FC<DateProps> = ({ date, relative }) => {
  const { locale } = useLocale(({ locale }) =>
    require(`../../i18n/${locale}.json`),
  );

  if (!date) {
    return <span>-</span>;
  }

  const dateToDisplay = displayDate(date, {
    format: VALID_DATE_FORMATS.DEFAULT,
    locale,
  });

  return (
    <OverflowTooltip
      content={<DateToolTipContent date={date} relative={relative} />}
      autoTipOnEllipsis
    >
      <div>
        {dateToDisplay}
        {relative && (
          <>
            <br />
            <DateRelativeText date={date} toolTipContent={false} />
          </>
        )}
      </div>
    </OverflowTooltip>
  );
};

DateCell.displayName = 'SubmittalsDateDisplay';
DateCell.propTypes = {
  date: PropTypes.instanceOf(Date),
  relative: PropTypes.bool,
};
DateCell.defaultProps = {
  relative: false,
};

export default DateCell;
